/**
 * Data export — flatten a form's submissions to a CSV/XLSX matrix.
 *
 * Two layouts:
 *   - `long`  : one row per submission instance (display_id, instance_index, …fields).
 *   - `wide`  : one row per record; repeatable instances become suffixed columns
 *               (wbc@1, wbc@2 …). Round-trips with the `repeatable_wide` import shape.
 *
 * Cells emit the STORED value (option_key, true/false, ISO date, number) so an
 * exported file re-imports cleanly. Reuses the data-explorer download helpers.
 */
import { supabase } from '@/lib/supabase/client'
import { downloadCsv, downloadXlsx } from '@/features/data-explorer/lib/export'
import type { FieldDefinition } from '@/features/form-builder/api/form-builder-types'

export type ExportLayout = 'long' | 'wide'
export type ExportFormat = 'csv' | 'xlsx'
export type HeaderStrategy = 'field_key' | 'label'

export interface SubmissionRecord {
  id: string
  record_id: string
  instance_index: number
  field_values: Record<string, unknown>
  display_id: string
  record_number: number
}

function cellToString(v: unknown): string {
  if (v == null) return ''
  if (Array.isArray(v)) return v.map(cellToString).join('،')
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export async function fetchSubmissions(formId: string): Promise<SubmissionRecord[]> {
  const { data, error } = await supabase
    .from('submissions')
    .select('id, record_id, instance_index, field_values, records!inner(display_id, record_number)')
    .eq('form_id', formId)
    .eq('is_deleted', false)
    .order('record_id')
    .order('instance_index')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => {
    const rec = r.records as { display_id: string; record_number: number }
    return {
      id: r.id as string,
      record_id: r.record_id as string,
      instance_index: r.instance_index as number,
      field_values: (r.field_values as Record<string, unknown>) ?? {},
      display_id: rec?.display_id ?? '',
      record_number: rec?.record_number ?? 0,
    }
  })
}

export interface ExportResult {
  headers: string[]
  matrix: string[][]
  rowCount: number
}

export interface BuildExportArgs {
  fields: FieldDefinition[]
  fieldKeys: string[]
  layout: ExportLayout
  headerStrategy: HeaderStrategy
}

function headerFor(fields: FieldDefinition[], key: string, strategy: HeaderStrategy): string {
  if (strategy === 'field_key') return key
  return fields.find((f) => f.field_key === key)?.label?.fa || key
}

export async function buildExport(formId: string, args: BuildExportArgs): Promise<ExportResult> {
  const subs = await fetchSubmissions(formId)
  return buildMatrix(subs, args)
}

/** Pure matrix builder — same logic as `buildExport`, but on already-fetched
 *  submissions so bulk export can fetch many forms then build each. */
export function buildMatrix(subs: SubmissionRecord[], args: BuildExportArgs): ExportResult {
  const { fields, fieldKeys, layout, headerStrategy } = args
  const label = (k: string) => headerFor(fields, k, headerStrategy)

  if (layout === 'long') {
    const headers = ['display_id', 'instance_index', ...fieldKeys.map(label)]
    const matrix = subs.map((s) => [
      s.display_id,
      String(s.instance_index),
      ...fieldKeys.map((k) => cellToString(s.field_values[k])),
    ])
    return { headers, matrix, rowCount: matrix.length }
  }

  // wide: group by record, instances → suffixed columns
  const byRecord = new Map<string, SubmissionRecord[]>()
  for (const s of subs) {
    if (!byRecord.has(s.record_id)) byRecord.set(s.record_id, [])
    byRecord.get(s.record_id)!.push(s)
  }
  const maxInstances = Math.max(1, ...[...byRecord.values()].map((arr) => arr.length))

  const headers = ['display_id']
  for (let i = 1; i <= maxInstances; i++) {
    for (const k of fieldKeys) headers.push(maxInstances > 1 ? `${label(k)}@${i}` : label(k))
  }

  const matrix: string[][] = []
  for (const [, instances] of byRecord) {
    const sorted = [...instances].sort((a, b) => a.instance_index - b.instance_index)
    const row = [sorted[0]?.display_id ?? '']
    for (let i = 0; i < maxInstances; i++) {
      const inst = sorted[i]
      for (const k of fieldKeys) row.push(inst ? cellToString(inst.field_values[k]) : '')
    }
    matrix.push(row)
  }
  return { headers, matrix, rowCount: matrix.length }
}

export function downloadExport(
  result: ExportResult,
  format: ExportFormat,
  filenameBase: string,
  meta: { formTitle: string; total: number },
) {
  const stamp = new Date().toISOString().slice(0, 10)
  const filename = `${filenameBase}-${stamp}.${format}`
  if (format === 'csv') {
    downloadCsv(filename, result.headers, result.matrix)
  } else {
    return downloadXlsx(
      filename,
      { headers: result.headers, matrix: result.matrix },
      {
        viewName: meta.formTitle,
        total: meta.total,
        generatedAt: new Date().toLocaleString('fa-IR'),
        filters: [],
      },
    )
  }
}
